import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Procedure, SasiType } from './Yubinuki';
import YubinukiIto from './YubinukiIto';

interface Props {
    index: number,
    tobi: number,
    procedure: Procedure,
    onChangeOffset: ((procedureIndex: number, offset: number) => void),
    onChangeSasiType: ((procedureIndex: number, sasiType: number) => void),
    onChangeMatomeSasi: ((procedureIndex: number, matomeSasi: boolean) => void),
    onChangeConnectedRounds: ((procedureIndex: number, connectedRounds: number) => void),

    onClickMoveUpProcedure: ((procedureIndex: number) => void),
    onClickMoveDownProcedure: ((procedureIndex: number) => void),
    onClickDeleteProcedure: ((procedureIndex: number) => void),

    onChangeItoColor: ((procedureIndex: number, itoIndex: number, color: string) => void),
    onChangeItoRounds: ((procedureIndex: number, itoIndex: number, rounds: number) => void),
    onClickMoveUpIto: ((procedureIndex: number, itoIndex: number) => void),
    onClickMoveDownIto: ((proccedureIndex: number, itoIndex: number) => void),
    onClickDeleteIto: ((procedureIndex: number, itoIndex: number) => void),
    onClickAddIto: ((procedureIndex: number) => void),
};

// [0...koma]
function createOffsetOptions(procedureIndex: number, tobi: number): Array<any> {
    return Array.from({ length: tobi + 1 }, (_v, k) => { return k }).map((v) => {
        return <option value={v} key={v}>{v}</option>
    });
}

// [1...50]
function createConnectedRoundsOptions(procedureIndex: number): Array<any> {
    return Array.from({ length: 50 }, (v, k) => { return k + 1 }).map((v) => {
        return <option value={v} key={v}>{v}</option>
    });
}

function YubinukiProcedure(props: Props) {
    const offsetOptions = useMemo(() => createOffsetOptions(props.index, props.tobi), [props.index, props.tobi]);
    const connectedRoundsOptions = useMemo(() => createConnectedRoundsOptions(props.index), [props.index]);

    function handleChangeOffset(e: React.FormEvent<HTMLSelectElement>) {
        props.onChangeOffset(props.index, parseInt(e.currentTarget.value));
    };
    function handleChangeSasiType(e: React.FormEvent<HTMLSelectElement>) {
        props.onChangeSasiType(props.index, parseInt(e.currentTarget.value));
    };
    function handleChangeMatomeSasi(e: React.FormEvent<HTMLInputElement>) {
        props.onChangeMatomeSasi(props.index, e.currentTarget.checked);
    };
    function handleChangeConnectedRounds(e: React.FormEvent<HTMLSelectElement>) {
        props.onChangeConnectedRounds(props.index, parseInt(e.currentTarget.value));
    };
    function handleChangeItoColor(itoIndex: number, color: string) {
        props.onChangeItoColor(props.index, itoIndex, color);
    };
    function handleChangeItoRounds(itoIndex: number, rounds: number) {
        props.onChangeItoRounds(props.index, itoIndex, rounds);
    };
    function handleMoveUpIto(itoIndex: number) {
        props.onClickMoveUpIto(props.index, itoIndex);
    };
    function handleMoveDownIto(itoIndex: number) {
        props.onClickMoveDownIto(props.index, itoIndex);
    };
    function handleDeleteIto(itoIndex: number) {
        props.onClickDeleteIto(props.index, itoIndex);
    };
    function handleMoveUp() {
        props.onClickMoveUpProcedure(props.index);
    }
    function handleMoveDown() {
        props.onClickMoveDownProcedure(props.index);
    }
    function handleDelete() {
        props.onClickDeleteProcedure(props.index);
    }
    function handleAddIto() {
        props.onClickAddIto(props.index);
    }

    let itos = props.procedure.itos.map((ito, index) => {
        return <YubinukiIto key={index} index={index} ito={ito}
            onChangeColor={handleChangeItoColor}
            onChangeRounds={handleChangeItoRounds}
            onMoveUp={handleMoveUpIto}
            onMoveDown={handleMoveDownIto}
            onRequestDelete={handleDeleteIto}
            />
    });

    return (
        <div className="procedure card mb-1">
            <div className="card-header">
                <div className="row">
                    <div className="col">
                        <h4>{`[${props.index + 1}]`}</h4>
                    </div>
                    <div className="col">
                        <div className="btn-toolbar float-right" role="toolbar">
                            <div className="btn-group mr-2" role="group">
                                <button type="button" name="moveUp" className="btn" onClick={handleMoveUp}>
                                    <FontAwesomeIcon icon="arrow-up" />
                                </button>
                                <button type="button" name="moveDown" className="btn" onClick={handleMoveDown}>
                                    <FontAwesomeIcon icon="arrow-down" />
                                </button>
                            </div>
                            <div className="btn-group" role="group">
                                <button type="button" name="trash" className="btn" onClick={handleDelete}>
                                    <FontAwesomeIcon icon="trash-alt" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <form>
                    <div className="form-row">
                        <div className="form-group col-sm-6 col-md-4">
                            <label htmlFor={`proc-${props.index}-offset`}>刺し位置：</label>
                            <select id={`proc-${props.index}-offset`} className="form-control" value={props.procedure.offset} onChange={handleChangeOffset}>
                                {offsetOptions}
                            </select>
                        </div>
                        <div className="from-group col-sm-6 col-md-4">
                            <label htmlFor={`proc-${props.index}-sasiType`}>向き：</label>
                            <select id={`proc-${props.index}-sasiType`} className="form-control" value={props.procedure.sasiType} onChange={handleChangeSasiType}>
                                <option value={SasiType.NAMI} key={SasiType.NAMI}>並み</option>
                                <option value={SasiType.HIRAKI} key={SasiType.HIRAKI}>開き</option>
                            </select>
                        </div>
                        <div className="form-group col-sm-6 col-md-4">
                            <label htmlFor={`proc-${props.index}-connectedRounds`}>続けて刺す段数：</label>
                            <select id={`proc-${props.index}-connectedRounds`} className="form-control" value={props.procedure.connectedRounds} onChange={handleChangeConnectedRounds}>
                                {connectedRoundsOptions}
                            </select>
                        </div>
                        <div className="form-group col-sm-6 col-md-4">
                            <div className="form-check form-check-inline">
                                <input type="checkbox" id={`proc-${props.index}-matomeSasi`} name="matomeSasi" className="form-check-input" checked={props.procedure.matomeSasi} onChange={handleChangeMatomeSasi} />
                                <label htmlFor={`proc-${props.index}-matomeSasi`} className="form-check-label">まとめて刺す</label>
                            </div>
                        </div>
                    </div>
                </form>
                <div className="row">
                    <div className="col-sm-12 col-md-3 mb-2">
                        <form>
                            <button type="button" name="addIto" className="btn btn-outline-secondary" onClick={handleAddIto}>
                                <FontAwesomeIcon icon="plus" />&nbsp;糸の色を追加する
                            </button>
                        </form>
                    </div>
                    <div className="col-sm-12 col-md-9">
                        <form>
                            {itos}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default YubinukiProcedure;
